import { useSetOrgSalesforceLink } from '@/features/SalesforceAccounts/api/salesforceAccountHooks'
import { SalesforceLinkFormConflict } from '@/features/SalesforceAccounts/components/LinkConflictPanel'
import {
  SalesforceAccountIdField,
  SalesforceReasonField,
} from '@/features/SalesforceAccounts/components/SalesforceLinkFields'
import { SalesforceLinkFormDialog } from '@/features/SalesforceAccounts/components/SalesforceLinkFormDialog'
import {
  expectedCurrentFields,
  useSalesforceLinkForm,
} from '@/features/SalesforceAccounts/hooks/useSalesforceLinkForm'
import { isValidSalesforceAccountId } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { Button } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SalesforceLinkFormProps } from './salesforceLinkFormProps'

/**
 * First link: the organisation has no Salesforce account yet.
 *
 * The ordinary submit carries **no** `expectedCurrentSalesforceAccountId` key
 * at all. A token supplied for an organisation that is actually unlinked is a
 * false assertion about the stored value and is refused as
 * `confirmation_required` rather than ignored, so sending an empty string, or
 * the prop, turns a legitimate first link into a 409.
 *
 * `reason` is optional here, matching the contract — but only for that first
 * attempt. A confirmed retry *does* send a token, and the contract requires a
 * non-blank reason whenever one is present, so the panel's confirm stays
 * disabled until there is one; otherwise the retry returns 400 `invalid`,
 * which replaces the conflict panel with a field error pointing at the id and
 * leaves the operator with no way forward.
 */
export function LinkAccountForm({
  orgId,
  currentAccountId,
  onSuccess,
  onReRead,
}: Readonly<SalesforceLinkFormProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const [typedId, setTypedId] = useState('')
  const [reason, setReason] = useState('')

  const trimmedId = typedId.trim()
  const isIdValid = isValidSalesforceAccountId(trimmedId)
  const trimmedReason = reason.trim()
  const hasReason = trimmedReason.length > 0

  const form = useSalesforceLinkForm({
    orgId,
    accountIds: [trimmedId, ...(currentAccountId ? [currentAccountId] : [])],
    toastId: 'salesforce-link-account',
    toastAccountId: trimmedId,
    onSuccess,
    onReRead,
    useLinkMutation: useSetOrgSalesforceLink,
    buildVariables: (token) => ({
      pathParams: { orgId },
      body: {
        salesforceAccountId: trimmedId,
        ...(trimmedReason ? { reason: trimmedReason } : {}),
        ...expectedCurrentFields(token),
      },
    }),
    canSubmit: isIdValid,
    canConfirm: isIdValid && hasReason,
  })

  const formatError =
    typedId.length > 0 && !isIdValid
      ? String(t('salesforceLink.actions.link.validation.format'))
      : undefined

  return (
    <SalesforceLinkFormDialog
      title={String(t('salesforceLink.actions.link.title'))}
      errorTitle={String(t('salesforceLink.actions.link.error.title'))}
      errorDescription={form.errorDescription}
      conflictPanel={
        form.isConflict ? (
          <SalesforceLinkFormConflict
            form={form}
            expectedAccountId={currentAccountId}
            confirmHint={
              hasReason
                ? undefined
                : String(t('salesforceLink.conflict.reasonRequired'))
            }
            variant="repoint"
          />
        ) : undefined
      }
      cancelLabel={String(t('salesforceLink.actions.link.cancel'))}
      submitButton={
        <Button
          colorPalette="brand"
          disabled={form.submitDisabled}
          loading={form.isBusy}
          onClick={() => form.submit()}
        >
          {String(t('salesforceLink.actions.link.submit'))}
        </Button>
      }
    >
      <SalesforceAccountIdField
        label={String(
          t('salesforceLink.actions.link.fields.salesforceAccountId.label'),
        )}
        placeholder={String(
          t(
            'salesforceLink.actions.link.fields.salesforceAccountId.placeholder',
          ),
        )}
        helper={String(
          t('salesforceLink.actions.link.fields.salesforceAccountId.helper'),
        )}
        value={typedId}
        error={form.fieldError ?? formatError}
        onChange={(value) => {
          form.clearFieldErrors()
          setTypedId(value)
        }}
      />

      <SalesforceReasonField
        label={String(t('salesforceLink.actions.link.fields.reason.label'))}
        placeholder={String(
          t('salesforceLink.actions.link.fields.reason.placeholder'),
        )}
        helper={String(t('salesforceLink.actions.link.fields.reason.helper'))}
        value={reason}
        onChange={(value) => {
          form.clearFieldErrors()
          setReason(value)
        }}
      />
    </SalesforceLinkFormDialog>
  )
}
